import { User, Plan } from '../types';
import { API_BASE_URL } from '../config/apiConfig';
import { getClientPlatform } from '../utils/environment';

export type { User, Plan };

interface ApiServiceConfig {
  baseUrl: string;
}

interface LoginResponse {
  message: string;
  token: string;
  user: User;
}

interface ProfileResponse {
  user: User;
}

interface AdminUsersResponse {
  users: Array<{
    id: number;
    email: string;
    name: string;
    plan: string;
    is_active: boolean;
    is_admin: boolean;
    email_verified: boolean;
    created_at: string;
    email_verification_token: string | null;
    model_usage_today?: Record<string, number>;
  }>;
}

interface AIConfig {
  id?: number;
  name: string;
  provider: string;
  apiUrl: string;
  apiKey?: string;
  model: string;
  prompts: {
    correction: string;
    translation: string;
    questionAnswering: string;
    generalChat: string;
  };
  isCustom?: boolean;
}

interface AISettingsResponse {
  configurations: AIConfig[];
  activeConfigId: number | null;
  useCustomSettings: boolean;
  activeConfig?: {
    id: number;
    name: string;
    apiUrl: string;
    apiKey?: string;
    model: string;
    prompts: {
      correction: string;
      translation: string;
      questionAnswering: string;
      generalChat: string;
    };
    isCustom: boolean;
  };
}

type PlatformName = 'macos' | 'windows' | 'linux' | 'android' | 'ios';

export interface AppPosition {
  id?: number;
  name?: string | null;
  bundleId?: string | null;
  processName?: string | null;
  platform: PlatformName | string;
  floatDx?: number;
  floatDy?: number;
  popupDx?: number;
  popupDy?: number;
  blockedDefault?: boolean;
}

class ApiService {
  private baseUrl: string;
  private token: string | null = null;

  constructor(config?: ApiServiceConfig) {
    this.baseUrl = config?.baseUrl || API_BASE_URL;
    
    // Load token from localStorage to maintain authentication state across windows
    const storedToken = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    if (storedToken) {
      this.token = storedToken;
    }
  }

  private async request(endpoint: string, options: RequestInit = {}): Promise<any> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const config = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Platform': getClientPlatform(),
        ...options.headers,
        ...(this.token && { 'Authorization': `Bearer ${this.token}` }),
      },
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    
    this.token = response.token;
    localStorage.setItem('token', response.token);
    return response;
  }

  async register(email: string, name: string, password: string): Promise<any> {
    const response = await this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, name, password }),
    });
    
    this.token = response.token;
    localStorage.setItem('token', response.token);
    return response;
  }

  // Google OAuth login - uses direct navigation for better compatibility
  initiateGoogleLogin(): void {
    // Android's main webview navigates away to the production backend just like
    // desktop does (same origin - see apiConfig.ts) - the server can't tell it
    // apart from a real kalimatai.com website visitor by Host header alone, so
    // it would otherwise redirect the success callback to the public website
    // instead of back into the app (tauri.localhost), leaving the app itself
    // never receiving the token. This marker round-trips through Google's OAuth
    // `state` param so /auth/google/callback knows to redirect to tauri.localhost
    // on Android, and lets it record which platform every Google login came from
    // (last_login_platform - see adminController.js/UserManagement.tsx) the same
    // way X-Client-Platform does for the request() path above - a redirect-based
    // flow can't carry a custom header, so `state` is the only channel available.
    const oauthUrl = `${this.baseUrl}/auth/google?platform=${getClientPlatform()}`;

    // For both Tauri and web, use direct navigation
    // This avoids CORS issues with window.open() in Tauri webview
    window.location.href = oauthUrl;
  }

  async getProfile(): Promise<ProfileResponse> {
    return this.request('/auth/profile');
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.getProfile();
    return response.user;
  }

  async upgradePlan(plan: string): Promise<any> {
    return this.request('/auth/upgrade-plan', {
      method: 'PUT',
      body: JSON.stringify({ plan }),
    });
  }

  async requestPasswordReset(email: string): Promise<any> {
    return this.request('/request-reset', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, password: string): Promise<any> {
    return this.request(`/reset/${token}`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  }

  async activateAccount(token: string): Promise<any> {
    return this.request(`/activate/${token}`, {
      method: 'GET',
    });
  }

  async getSettings(): Promise<any> {
    return this.request('/settings');
  }

  async updateSettings(settings: any): Promise<any> {
    return this.request('/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  // AI Settings methods
  async getAISettings(): Promise<AISettingsResponse> {
    return this.request('/ai-settings');
  }

  async updateAISettings(settings: any): Promise<AISettingsResponse> {
    return this.request('/ai-settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  async createAIConfiguration(config: Omit<AIConfig, 'id'>): Promise<any> {
    return this.request('/ai-settings', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  }

  async updateAIConfiguration(id: number, config: Omit<AIConfig, 'id'>): Promise<any> {
    return this.request(`/ai-settings/${id}`, {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  async deleteAIConfiguration(id: number): Promise<any> {
    return this.request(`/ai-settings/${id}`, {
      method: 'DELETE',
    });
  }

  async setActiveAIConfiguration(configId: number | null): Promise<any> {
    const endpoint = configId ? `/ai-settings/active/${configId}` : '/ai-settings/active/0';
    return this.request(endpoint, {
      method: 'POST',
    });
  }

  // Admin AI Settings methods for managing default configurations
  async getAdminAIDefaults(): Promise<AISettingsResponse> {
    return this.request('/ai-settings/defaults');
  }

  async createAdminAIDefault(config: Omit<AIConfig, 'id'>): Promise<any> {
    return this.request('/ai-settings/defaults', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  }

  async updateAdminAIDefault(id: number, config: Omit<AIConfig, 'id'>): Promise<any> {
    return this.request(`/ai-settings/defaults/${id}`, {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  async deleteAdminAIDefault(id: number): Promise<any> {
    return this.request(`/ai-settings/defaults/${id}`, {
      method: 'DELETE',
    });
  }

  // History methods
  async getHistory(): Promise<any> {
    return this.request('/history');
  }

  async deleteFromHistory(id: number): Promise<any> {
    return this.request(`/history/${id}`, {
      method: 'DELETE',
    });
  }

  async addToHistory(text: string, result: string, action: string): Promise<any> {
    return this.request('/history', {
      method: 'POST',
      body: JSON.stringify({ text, result, action }),
    });
  }

  // Admin methods
  async getAdminUsers(): Promise<AdminUsersResponse> {
    return this.request('/admin/users');
  }

  async updateAdminUserStatus(userId: number, isActive: boolean): Promise<any> {
    return this.request(`/admin/users/${userId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ isActive }),
    });
  }

  async updateAdminUserRole(userId: number, isAdmin: boolean): Promise<any> {
    return this.request(`/admin/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ isAdmin }),
    });
  }

  async updateAdminUserPlan(userId: number, plan: string): Promise<any> {
    return this.request(`/admin/users/${userId}/plan`, {
      method: 'PUT',
      body: JSON.stringify({ plan }),
    });
  }

  async updateAdminUser(userId: number, updates: Partial<any>): Promise<any> {
    return this.request(`/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async verifyAdminUserEmail(userId: number): Promise<any> {
    return this.request(`/admin/users/${userId}/verify-email`, {
      method: 'PUT',
    });
  }

  async getAdminUserActivationLink(userId: number): Promise<{ verified: boolean; activationLink: string | null }> {
    return this.request(`/admin/users/${userId}/activation-link`);
  }

  async getAdminUserPasswordResetLink(userId: number): Promise<{ resetLink: string; expiresAt: string; userEmail: string; userName: string }> {
    return this.request(`/admin/users/${userId}/password-reset-link`);
  }

  async deleteAdminUser(userId: number): Promise<any> {
    return this.request(`/admin/users/${userId}`, {
      method: 'DELETE'
    });
  }

  // Plan management methods
  async getPlans(): Promise<any> {
    return this.request('/admin/plans');
  }

  async createPlan(plan: Omit<Plan, 'id'>): Promise<any> {
    return this.request('/admin/plans', {
      method: 'POST',
      body: JSON.stringify(plan),
    });
  }

  async updatePlan(id: number, plan: Omit<Plan, 'id'>): Promise<any> {
    return this.request(`/admin/plans/${id}`, {
      method: 'PUT',
      body: JSON.stringify(plan),
    });
  }

  async deletePlan(id: number): Promise<any> {
    return this.request(`/admin/plans/${id}`, {
      method: 'DELETE',
    });
  }

  // App positions (public)
  async getAppPositions(platform?: string): Promise<{ appPositions: AppPosition[] }> {
    const qs = platform ? `?platform=${encodeURIComponent(platform)}` : '';
    return this.request(`/app-positions${qs}`);
  }

  // Admin app positions
  async getAdminAppPositions(): Promise<{ appPositions: AppPosition[] }> {
    return this.request('/admin/app-positions');
  }

  async createAdminAppPosition(pos: AppPosition): Promise<{ appPosition: AppPosition }> {
    return this.request('/admin/app-positions', {
      method: 'POST',
      body: JSON.stringify({
        name: pos.name ?? null,
        bundleId: pos.bundleId ?? null,
        processName: pos.processName ?? null,
        platform: pos.platform,
        floatDx: pos.floatDx ?? 0,
        floatDy: pos.floatDy ?? 0,
        popupDx: pos.popupDx ?? 0,
        popupDy: pos.popupDy ?? 0,
        blockedDefault: !!pos.blockedDefault,
      }),
    });
  }

  async updateAdminAppPosition(id: number, pos: AppPosition): Promise<{ appPosition: AppPosition }> {
    return this.request(`/admin/app-positions/${id}`, {
      method: 'PUT',
      body: JSON.stringify({
        name: pos.name ?? null,
        bundleId: pos.bundleId ?? null,
        processName: pos.processName ?? null,
        platform: pos.platform,
        floatDx: pos.floatDx ?? 0,
        floatDy: pos.floatDy ?? 0,
        popupDx: pos.popupDx ?? 0,
        popupDy: pos.popupDy ?? 0,
        blockedDefault: !!pos.blockedDefault,
      }),
    });
  }

  async deleteAdminAppPosition(id: number): Promise<{ success: boolean }> {
    return this.request(`/admin/app-positions/${id}`, {
      method: 'DELETE',
    });
  }

  // User notification methods
  async getNotifications(): Promise<{ notifications: any[]; unread_count: number }> {
    return this.request('/notifications');
  }

  async getUnreadNotificationCount(): Promise<{ unread_count: number }> {
    return this.request('/notifications/unread-count');
  }

  async markNotificationAsRead(notificationId: number): Promise<any> {
    return this.request(`/notifications/${notificationId}/read`, {
      method: 'PUT',
    });
  }

  async markAllNotificationsAsRead(): Promise<any> {
    return this.request('/notifications/read-all', {
      method: 'PUT',
    });
  }

  // Content reporting method
  async reportContent(data: {
    content: string;
    contentType: 'correction' | 'translation' | 'answer';
    reason: string;
    description?: string;
    originalText?: string;
  }): Promise<any> {
    return this.request('/report-content', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Image upload methods
  async checkImageLimit(): Promise<{
    canUpload: boolean;
    maxImagesPerDay: number;
    maxImageSizeMb: number;
    currentUsage: number;
    remaining: number;
    reason?: string;
    message?: string;
  }> {
    return this.request('/auth/image-limit');
  }

  async incrementImageUsage(): Promise<any> {
    return this.request('/auth/image-usage', {
      method: 'POST',
    });
  }

  async getImagePlanLimits(): Promise<{
    maxImagesPerDay: number;
    maxImageSizeMb: number;
    enabled: boolean;
  }> {
    return this.request('/auth/image-plan-limits');
  }

  // Admin content reports methods
  async getAdminContentReports(): Promise<{ reports: Array<any> }> {
    return this.request('/report-content/admin');
  }

  async deleteAdminContentReport(reportId: number): Promise<any> {
    return this.request(`/report-content/admin/${reportId}`, {
      method: 'DELETE'
    });
  }

  setToken(token: string) {
    this.token = token;
  }

  getToken() {
    return this.token;
  }
}

export const apiService = new ApiService();